(() => {
  'use strict';

  const CONFIG_SELECTOR = 'script[data-product-set-cart-repair]';
  const CONFIG_MARKERS = ['_sm_config_source'];
  const REPAIR_SOURCE = 'legacy_cart_default_repair';
  let repairPromise = null;

  const normalise = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');
  const normaliseKey = (value) => String(value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
  const normaliseCountry = (value) => {
    const country = normalise(value).toLocaleLowerCase();
    if (['at', 'austria', 'österreich', 'oesterreich'].includes(country)) return 'at';
    if (['de', 'germany', 'deutschland'].includes(country)) return 'de';
    return country;
  };

  function hasConfiguration(properties = {}) {
    return Object.keys(properties || {}).some((key) =>
      CONFIG_MARKERS.includes(key) || key.startsWith('_sm_cfg_')
    );
  }

  function resolveItem(item, country) {
    const variants = Array.isArray(item.countryVariants) ? item.countryVariants : [];
    if (!country || variants.length === 0) return { ...item };

    const matched = variants.find((variant) => normaliseCountry(variant.country) === country);
    return {
      ...item,
      variantId: matched?.variantId || null,
      available: item.isDefault ? true : Boolean(matched?.available)
    };
  }

  function buildRepairPlan(config, cartLine, lineKey) {
    if (!config || !cartLine || hasConfiguration(cartLine.properties)) return null;

    const optionNames = Array.isArray(config.optionNames) ? config.optionNames : [];
    const selectedOptions = Array.isArray(config.selectedOptions) ? config.selectedOptions : [];
    const optionIndex = optionNames.findIndex(
      (name) => normalise(name).toLocaleLowerCase() === normalise(config.optionName).toLocaleLowerCase()
    );
    const countryIndex = optionNames.findIndex(
      (name) => normalise(name).toLocaleLowerCase() === 'lieferland'
    );
    if (optionIndex < 0) return null;

    const leistung = normaliseKey(selectedOptions[optionIndex]);
    const country = countryIndex >= 0 ? normaliseCountry(selectedOptions[countryIndex]) : '';
    const matchingGroups = (Array.isArray(config.groups) ? config.groups : []).filter((group) =>
      String(group.leistungValue ?? '').split(',').map(normaliseKey).filter(Boolean).includes(leistung) &&
      Array.isArray(group.items) && group.items.length > 0
    );
    if (matchingGroups.length === 0) return null;

    const defaults = matchingGroups.flatMap((group) => {
      const items = group.items.map((item) => resolveItem(item, country));
      const item = items.find((candidate) => candidate.isDefault && candidate.available);
      return item && group.title && item.title ? [{ group, item }] : [];
    });
    if (defaults.length === 0) return null;

    const setId = cartLine.properties?._sm_set_id || `legacy-${String(lineKey)}`;
    const properties = { ...(cartLine.properties || {}), _sm_set_id: setId, _sm_config_source: REPAIR_SOURCE };
    let zubehorIndex = 0;
    defaults.forEach(({ group, item }) => {
      // 有独立升级行的组不写 Zubehör, 与 product-set-dialog.js 一致
      if (!(item.variantId && item.available)) {
        zubehorIndex += 1;
        properties[`Zubehör ${zubehorIndex}`] = item.title;
      }
      properties[`_sm_cfg_${group.title}`] = item.title;
    });

    const seenVariants = new Set();
    const gifts = [];
    defaults.slice().reverse().forEach(({ group, item }) => {
      if (!item.variantId || !item.available) return;
      const variantId = String(item.variantId);
      if (seenVariants.has(variantId)) return;
      seenVariants.add(variantId);
      const quantityPerSet = Math.max(1, Number(item.quantity ?? item.quantityPerSet) || 1);
      gifts.push({
        id: Number(item.variantId),
        quantity: Math.max(1, Number(cartLine.quantity) || 1) * quantityPerSet,
        properties: {
          _sm_set_id: setId,
          _sm_set_parent_variant_id: String(cartLine.variant_id || config.selectedVariantId || ''),
          _sm_set_group: group.title || String(group.id || ''),
          _sm_set_selection: item.title,
          _sm_set_quantity_per_set: String(quantityPerSet)
        }
      });
    });

    return { lineKey, setId, properties, gifts };
  }

  function filterMissingGifts(gifts, cartItems, setId) {
    const existingGiftVariants = new Set(
      (Array.isArray(cartItems) ? cartItems : [])
        .filter((item) => item.properties?._sm_set_id === setId && item.properties?._sm_set_group)
        .map((item) => String(item.variant_id))
    );
    return gifts.filter((gift) => !existingGiftVariants.has(String(gift.id)));
  }

  function cartRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  async function requestCart(path, body) {
    const response = await fetch(`${cartRoot()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart repair failed.');
    return data;
  }

  function setCheckoutState(busy) {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((button) => {
      if (busy) {
        button.dataset.productSetRepairDisabled = button.disabled ? 'preserve' : 'repair';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else if (button.dataset.productSetRepairDisabled) {
        if (button.dataset.productSetRepairDisabled === 'repair') button.disabled = false;
        button.removeAttribute('aria-busy');
        delete button.dataset.productSetRepairDisabled;
      }
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      container.toggleAttribute('inert', busy);
      container.setAttribute('aria-busy', String(busy));
    });
  }

  function showError() {
    if (document.querySelector('[data-product-set-repair-error]')) return;
    const message = document.createElement('p');
    message.dataset.productSetRepairError = '';
    message.className = 'alert alert--error text-sm';
    message.setAttribute('role', 'alert');
    message.textContent = 'Die enthaltenen Set-Artikel konnten nicht aktualisiert werden. Bitte laden Sie die Seite neu oder konfigurieren Sie das Produkt erneut.';
    const checkout = document.querySelector('button[name="checkout"], input[name="checkout"]');
    (checkout?.closest('form, .grid') || document.querySelector('cart-items'))?.prepend(message);
  }

  async function repairLegacyCart() {
    const elements = Array.from(document.querySelectorAll(CONFIG_SELECTOR));
    if (elements.length === 0) return false;

    setCheckoutState(true);
    try {
      const cart = await requestCart('cart.js');
      let repaired = false;

      for (const element of elements) {
        let config;
        try {
          config = JSON.parse(element.textContent || '');
        } catch (error) {
          throw new Error(`Invalid product-set configuration: ${error.message}`);
        }

        const lineKey = element.dataset.cartLineKey;
        const line = cart.items.find((item) => item.key === lineKey);
        if (!line || hasConfiguration(line.properties)) continue;
        const plan = buildRepairPlan(config, line, lineKey);
        if (!plan) throw new Error(`No default configuration matches cart line ${lineKey}.`);

        const missingGifts = filterMissingGifts(plan.gifts, cart.items, plan.setId);
        if (missingGifts.length > 0) await requestCart('cart/add.js', { items: missingGifts });
        await requestCart('cart/change.js', {
          id: plan.lineKey,
          quantity: line.quantity,
          properties: plan.properties
        });
        repaired = true;
      }

      if (repaired) window.location.reload();
      else setCheckoutState(false);
      return repaired;
    } catch (error) {
      console.error('[Product set cart repair] Repair failed.', error);
      showError();
      return false;
    }
  }

  function scheduleRepair() {
    if (repairPromise) return repairPromise;
    repairPromise = repairLegacyCart().finally(() => {
      repairPromise = null;
    });
    return repairPromise;
  }

  const api = { buildRepairPlan, filterMissingGifts, hasConfiguration, normaliseCountry, normaliseKey, resolveItem };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    document.addEventListener('cart:updated', scheduleRepair);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleRepair, { once: true });
    else scheduleRepair();
  }
})();
